/**
 * Thermal 80 mm receipt helpers.
 *
 * • openReceipt(orderId, options) — fetches the branded receipt HTML from the
 *   backend and opens it in a small popup window, letting the browser fire
 *   its native Print dialog (works with any thermal driver installed on the
 *   workstation).
 *
 *   `options.variant` picks the receipt view:
 *     · `"auto"`    (default) — deposit while there is an unpaid balance,
 *                    final tax invoice once the order is closed.
 *     · `"deposit"` — force the "Deposit Receipt" header, no PAID badge.
 *                    Auto-fired from BranchOrders after every partial payment.
 *     · `"final"`   — force the "Tax Invoice" header + PAID badge.
 *                    Auto-fired from BranchOrders after Close Order.
 *
 * • emailReceipt(orderId) — sends / re-sends the same receipt to the customer's
 *   email. Shows a toast on success / failure.
 */
import { staffApi, formatApiError } from "./api";
import { toast } from "sonner";

export async function openReceipt(orderId, options = {}) {
  const variant = options.variant || "auto";
  try {
    const r = await staffApi.get(`/orders/${orderId}/receipt.html`, {
      responseType: "text",
      params: { variant },
    });
    const w = window.open("", "cocoon-receipt", "width=360,height=720,menubar=no,toolbar=no,location=no,noopener=no");
    if (!w) {
      toast.error("Pop-ups are blocked — allow pop-ups to print the receipt.");
      return;
    }
    // Detach opener reference so nothing in the receipt window can navigate
    // or script the POS window even though we still need to write into it.
    try { w.opener = null; } catch (_) { /* cross-origin safety */ }
    w.document.open();
    w.document.write(r.data);
    w.document.close();
    // The receipt HTML already schedules window.print() on load, but call
    // focus() so the print dialog surfaces on top of the POS window.
    w.focus();
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

export async function emailReceipt(orderId) {
  try {
    const r = await staffApi.post(`/orders/${orderId}/receipt/email`);
    if (r.data?.ok) toast.success(`Receipt emailed to ${r.data.to}`);
    else toast.warning(`Attempted to send to ${r.data.to} — email service may be offline.`);
  } catch (e) {
    toast.error(formatApiError(e));
  }
}
